/**
 * Graphiques SVG, générés en JavaScript comme dans le document source.
 *
 * Chaque graphique est doublé d'un tableau de données replié, accessible aux
 * lecteurs d'écran : un `<svg>` seul n'est pas lisible autrement.
 */

import { entier, euros, keuros } from '@scc/core';
import { h, s } from './dom.js';

/**
 * Couleurs lues dans la feuille de style, et non écrites ici.
 *
 * Un graphique change de teintes avec le thème : grille, axes, étiquettes et
 * séries sont donc des jetons CSS. Les valeurs sont mises en cache tant que le
 * thème ne change pas — `getComputedStyle` à chaque trait coûterait cher sur
 * une page qui en porte plusieurs milliers.
 */
const cache = new Map<string, string>();
let themeEnCache = '';

function jeton(nom: string, repli: string): string {
  const theme = document.documentElement.dataset['theme'] ?? '';
  if (theme !== themeEnCache) {
    cache.clear();
    themeEnCache = theme;
  }
  const enCache = cache.get(nom);
  if (enCache !== undefined) return enCache;
  const v = getComputedStyle(document.documentElement).getPropertyValue(nom).trim();
  const valeur = v === '' ? repli : v;
  cache.set(nom, valeur);
  return valeur;
}

/** Structure d'un graphique : ce qui n'est pas une donnée. */
const STRUCTURE = {
  get grille() { return jeton('--chart-grille', '#E8E8F0'); },
  get axe() { return jeton('--chart-axe', '#C8C8D8'); },
  get etiquette() { return jeton('--chart-etiquette', '#6A6A8A'); },
  get fond() { return jeton('--chart-fond', '#ffffff'); },
};

export interface Serie {
  readonly nom: string;
  readonly couleur: string;
  readonly valeurs: readonly (number | null)[];
  readonly epaisseur?: number;
  readonly pointilles?: string;
}

export type Unite = 'keuros' | 'euros' | 'effectif';

function formater(v: number, unite: Unite): string {
  if (unite === 'effectif') return entier(Math.round(v));
  return unite === 'euros' ? euros(v) : keuros(v);
}

export interface OptionsLigne {
  readonly unite?: Unite;
  readonly bande?: readonly number[];
  readonly nomBande?: string;
  readonly depuisZero?: boolean;
  readonly titre: string;
  readonly enteteAbscisse: string;
}

/** Graphique en lignes. */
export function graphiqueLignes(
  annees: readonly (number | string)[],
  series: readonly Serie[],
  o: OptionsLigne,
): HTMLElement {
  const unite = o.unite ?? 'keuros';
  const L = 880;
  const H = 340;
  const MG = 78;
  const MD = 26;
  const MH = 22;
  const MB = 44;

  const toutes: number[] = [];
  for (const se of series) for (const v of se.valeurs) if (v !== null && v !== undefined) toutes.push(v);
  if (o.bande) toutes.push(...o.bande);
  if (toutes.length === 0) return h('div', { classe: 'chart-wrap' });

  let min = Math.min(...toutes);
  let max = Math.max(...toutes);
  const minDonnees = min;
  if (o.depuisZero) min = Math.min(min, 0);
  const amplitude = max - min || 1;
  max += amplitude * 0.1;
  min -= amplitude * 0.1;
  if (o.depuisZero && minDonnees >= 0 && min < 0) min = 0;

  const X = (i: number): number =>
    MG + (L - MG - MD) * (annees.length > 1 ? i / (annees.length - 1) : 0);
  const Y = (v: number): number => MH + (H - MH - MB) * (1 - (v - min) / (max - min));

  const svg = s('svg', {
    viewBox: `0 0 ${L} ${H}`,
    role: 'img',
    'aria-label': o.titre,
    'font-family': 'DM Sans, sans-serif',
  });
  svg.appendChild(s('title', {}, o.titre));

  for (let k = 0; k <= 5; k++) {
    const v = min + ((max - min) * k) / 5;
    const y = Y(v);
    svg.appendChild(
      s('line', { x1: MG, y1: y.toFixed(1), x2: L - MD, y2: y.toFixed(1), stroke: STRUCTURE.grille, 'stroke-width': 1 }),
    );
    svg.appendChild(
      s(
        'text',
        { x: MG - 10, y: (y + 4).toFixed(1), 'text-anchor': 'end', 'font-size': 11, fill: STRUCTURE.etiquette },
        formater(v, unite),
      ),
    );
  }

  if (o.bande) {
    let haut = '';
    for (let i = 0; i < annees.length; i++) {
      haut += `${X(i).toFixed(1)},${Y(o.bande[i] ?? 0).toFixed(1)} `;
    }
    const bas = `${X(annees.length - 1).toFixed(1)},${Y(min).toFixed(1)} ${X(0).toFixed(1)},${Y(min).toFixed(1)}`;
    svg.appendChild(s('polygon', { points: haut + bas, fill: TEINTES.danger, opacity: 0.14 }));
    svg.appendChild(
      s('polyline', {
        points: haut,
        fill: 'none',
        stroke: TEINTES.danger,
        'stroke-width': 1.5,
        'stroke-dasharray': '5 4',
        opacity: 0.6,
      }),
    );
  }

  for (let j = 0; j < annees.length; j++) {
    svg.appendChild(
      s(
        'text',
        { x: X(j).toFixed(1), y: H - MB + 22, 'text-anchor': 'middle', 'font-size': 11.5, fill: STRUCTURE.etiquette },
        String(annees[j]),
      ),
    );
  }
  svg.appendChild(
    s('line', {
      x1: MG,
      y1: (H - MB).toFixed(1),
      x2: L - MD,
      y2: (H - MB).toFixed(1),
      stroke: STRUCTURE.axe,
      'stroke-width': 1,
    }),
  );

  for (const se of series) {
    const points: string[] = [];
    const pastilles: SVGElement[] = [];
    for (let i = 0; i < annees.length; i++) {
      const v = se.valeurs[i];
      if (v === null || v === undefined) continue;
      points.push(`${X(i).toFixed(1)},${Y(v).toFixed(1)}`);
      const c = s('circle', {
        cx: X(i).toFixed(1),
        cy: Y(v).toFixed(1),
        r: 4,
        fill: STRUCTURE.fond,
        stroke: se.couleur,
        'stroke-width': 2,
      });
      c.appendChild(
        s(
          'title',
          {},
          `${annees[i]} — ${se.nom} : ${unite === 'effectif' ? `${entier(Math.round(v))} adhérents` : euros(v)}`,
        ),
      );
      pastilles.push(c);
    }
    svg.appendChild(
      s('polyline', {
        points: points.join(' '),
        fill: 'none',
        stroke: se.couleur,
        'stroke-width': se.epaisseur ?? 2.5,
        'stroke-linejoin': 'round',
        'stroke-linecap': 'round',
        ...(se.pointilles ? { 'stroke-dasharray': se.pointilles } : {}),
      }),
    );
    for (const p of pastilles) svg.appendChild(p);
  }

  return h(
    'div',
    { classe: 'chart-wrap' },
    svg,
    tableauDonnees(o.titre, o.enteteAbscisse, annees, series, unite, o.bande, o.nomBande),
  );
}

/** Graphique en barres groupées : produits contre charges. */
export function graphiqueBarres(
  annees: readonly number[],
  a: { nom: string; couleur: string; valeurs: readonly number[] },
  b: { nom: string; couleur: string; valeurs: readonly number[] },
  titre: string,
): HTMLElement {
  const L = 880;
  const H = 300;
  const MG = 78;
  const MD = 26;
  const MH = 20;
  const MB = 44;
  const max = Math.max(...a.valeurs, ...b.valeurs) * 1.12 || 1;
  const Y = (v: number): number => MH + (H - MH - MB) * (1 - v / max);
  const creneau = (L - MG - MD) / annees.length;
  const largeur = Math.min(34, creneau / 3.2);

  const svg = s('svg', {
    viewBox: `0 0 ${L} ${H}`,
    role: 'img',
    'aria-label': titre,
    'font-family': 'DM Sans, sans-serif',
  });
  svg.appendChild(s('title', {}, titre));

  for (let k = 0; k <= 4; k++) {
    const v = (max * k) / 4;
    const y = Y(v);
    svg.appendChild(s('line', { x1: MG, y1: y.toFixed(1), x2: L - MD, y2: y.toFixed(1), stroke: STRUCTURE.grille }));
    svg.appendChild(
      s('text', { x: MG - 10, y: (y + 4).toFixed(1), 'text-anchor': 'end', 'font-size': 11, fill: STRUCTURE.etiquette }, keuros(v)),
    );
  }

  for (let i = 0; i < annees.length; i++) {
    const cx = MG + creneau * (i + 0.5);
    for (const [serie, decalage] of [
      [a, -largeur - 3],
      [b, 3],
    ] as const) {
      const v = serie.valeurs[i] ?? 0;
      const r = s('rect', {
        x: (cx + decalage).toFixed(1),
        y: Y(v).toFixed(1),
        width: largeur.toFixed(1),
        height: (Y(0) - Y(v)).toFixed(1),
        rx: 3,
        fill: serie.couleur,
      });
      r.appendChild(s('title', {}, `${annees[i]} — ${serie.nom} : ${euros(v)}`));
      svg.appendChild(r);
    }
    svg.appendChild(
      s(
        'text',
        { x: cx.toFixed(1), y: H - MB + 22, 'text-anchor': 'middle', 'font-size': 11.5, fill: STRUCTURE.etiquette },
        String(annees[i]),
      ),
    );
  }
  svg.appendChild(
    s('line', { x1: MG, y1: Y(0).toFixed(1), x2: L - MD, y2: Y(0).toFixed(1), stroke: STRUCTURE.axe }),
  );

  return h(
    'div',
    { classe: 'chart-wrap' },
    svg,
    tableauDonnees(
      titre,
      'Exercice',
      annees,
      [
        { nom: a.nom, couleur: a.couleur, valeurs: a.valeurs },
        { nom: b.nom, couleur: b.couleur, valeurs: b.valeurs },
      ],
      'euros',
    ),
  );
}

/** Tableau de données doublant le graphique, pour les lecteurs d'écran. */
function tableauDonnees(
  titre: string,
  enteteAbscisse: string,
  annees: readonly (number | string)[],
  series: readonly Serie[],
  unite: Unite,
  bande?: readonly number[],
  nomBande?: string,
): HTMLElement {
  const colonnes = [...series.map((x) => x.nom), ...(bande ? [nomBande ?? 'Seuil'] : [])];
  const thead = h(
    'thead',
    {},
    h('tr', {}, h('th', { scope: 'col' }, enteteAbscisse), ...colonnes.map((c) => h('th', { scope: 'col' }, c))),
  );
  const tbody = h(
    'tbody',
    {},
    ...annees.map((an, i) =>
      h(
        'tr',
        {},
        h('th', { scope: 'row' }, String(an)),
        ...series.map((se) => {
          const v = se.valeurs[i];
          return h('td', {}, v === null || v === undefined ? '—' : formater(v, unite));
        }),
        ...(bande ? [h('td', {}, formater(bande[i] ?? 0, unite))] : []),
      ),
    ),
  );
  return h(
    'details',
    { classe: 'donnees-graphique' },
    h('summary', {}, `Données du graphique « ${titre} »`),
    h('table', {}, h('caption', { classe: 'invisible' }, titre), thead, tbody),
  );
}

/** Légende d'un graphique. */
export function legende(entrees: readonly { nom: string; couleur: string }[]): HTMLElement {
  return h(
    'div',
    { classe: 'legend' },
    ...entrees.map((e) => h('span', {}, h('i', { '--teinte': e.couleur, 'aria-hidden': 'true' }), e.nom)),
  );
}

/**
 * Teintes de la charte, exposées aux vues pour éviter toute redéfinition.
 *
 * Ce sont des accesseurs et non des constantes : la valeur se relit dans la
 * feuille de style à chaque usage, et suit donc le thème. `charcoal` désigne
 * ici une **couleur de trait**, pas une surface — en mode nuit elle s'éclaircit.
 */
export const TEINTES = {
  get charcoal() { return jeton('--serie-1', '#1A1A2E'); },
  get slate() { return jeton('--serie-6', '#3D3D5C'); },
  get green() { return jeton('--serie-2', '#1B7A5F'); },
  get amber() { return jeton('--serie-4', '#8A4008'); },
  get rose() { return jeton('--serie-5', '#E8185A'); },
  get danger() { return jeton('--danger', '#B5342A'); },
  get blue() { return jeton('--serie-3', '#1E5A9E'); },
};

/**
 * Palette de répartition, ordonnée du plus au moins saillant.
 * Une part au-delà de la huitième reprend la première teinte : au-delà de huit
 * parts, un anneau n'est de toute façon plus lisible et la vue doit regrouper.
 */
const NB_SERIES = 8;

/** Teinte d'une série, par son rang. Au-delà de huit, la palette se répète. */
export function teinte(rang: number): string {
  const i = (rang % NB_SERIES) + 1;
  return jeton(`--serie-${i}`, '#1A1A2E');
}

/** La palette complète, dans l'ordre. */
export function palette(): string[] {
  return Array.from({ length: NB_SERIES }, (_, i) => teinte(i));
}

/** Tableau de données générique doublant un graphique non temporel. */
function tableauSimple(
  titre: string,
  colonnes: readonly string[],
  lignes: readonly (readonly string[])[],
): HTMLElement {
  return h(
    'details',
    { classe: 'donnees-graphique' },
    h('summary', {}, `Données du graphique « ${titre} »`),
    h(
      'table',
      {},
      h('caption', { classe: 'invisible' }, titre),
      h('thead', {}, h('tr', {}, ...colonnes.map((c) => h('th', { scope: 'col' }, c)))),
      h(
        'tbody',
        {},
        ...lignes.map((l) =>
          h('tr', {}, ...l.map((c, i) => (i === 0 ? h('th', { scope: 'row' }, c) : h('td', {}, c)))),
        ),
      ),
    ),
  );
}

/** Chemin SVG d'un secteur d'anneau, angles en radians depuis midi. */
function secteurAnneau(
  cx: number,
  cy: number,
  rExt: number,
  rInt: number,
  angleDebut: number,
  angleFin: number,
): string {
  const p = (r: number, a: number): [number, number] => [
    cx + r * Math.cos(a - Math.PI / 2),
    cy + r * Math.sin(a - Math.PI / 2),
  ];
  const grand = angleFin - angleDebut > Math.PI ? 1 : 0;
  const [x1, y1] = p(rExt, angleDebut);
  const [x2, y2] = p(rExt, angleFin);
  const [x3, y3] = p(rInt, angleFin);
  const [x4, y4] = p(rInt, angleDebut);
  return [
    `M ${x1.toFixed(2)} ${y1.toFixed(2)}`,
    `A ${rExt} ${rExt} 0 ${grand} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`,
    `L ${x3.toFixed(2)} ${y3.toFixed(2)}`,
    `A ${rInt} ${rInt} 0 ${grand} 0 ${x4.toFixed(2)} ${y4.toFixed(2)}`,
    'Z',
  ].join(' ');
}

export interface PartGraphique {
  readonly cle: string;
  readonly libelle: string;
  readonly montant: number;
  readonly part: number;
}

/**
 * Anneau de répartition, avec le total au centre et une légende chiffrée.
 * Une part unique occupant tout le tour est dessinée comme un cercle plein :
 * un arc de 2π ne se trace pas, ses deux extrémités se confondent.
 */
export function graphiqueAnneau(
  parts: readonly PartGraphique[],
  o: { titre: string; total: number; sousTitre: string; enteteAbscisse?: string },
): HTMLElement {
  const L = 880;
  const H = 320;
  const CX = 190;
  const CY = H / 2;
  const R_EXT = 118;
  const R_INT = 74;

  const svg = s('svg', {
    viewBox: `0 0 ${L} ${H}`,
    role: 'img',
    'aria-label': o.titre,
    'font-family': 'DM Sans, sans-serif',
  });
  svg.appendChild(s('title', {}, o.titre));

  if (parts.length === 1 && parts[0]) {
    const seule = parts[0];
    const anneau = s('circle', {
      cx: CX,
      cy: CY,
      r: (R_EXT + R_INT) / 2,
      fill: 'none',
      stroke: teinte(0),
      'stroke-width': R_EXT - R_INT,
    });
    anneau.appendChild(s('title', {}, `${seule.libelle} : ${euros(seule.montant)}`));
    svg.appendChild(anneau);
  } else {
    let angle = 0;
    parts.forEach((p, i) => {
      const fin = angle + p.part * 2 * Math.PI;
      const chemin = s('path', {
        d: secteurAnneau(CX, CY, R_EXT, R_INT, angle, fin),
        fill: teinte(i),
      });
      chemin.appendChild(
        s('title', {}, `${p.libelle} : ${euros(p.montant)} (${(p.part * 100).toFixed(1)} %)`),
      );
      svg.appendChild(chemin);
      angle = fin;
    });
  }

  svg.appendChild(
    s(
      'text',
      { x: CX, y: CY - 2, 'text-anchor': 'middle', 'font-size': 25, 'font-weight': 600, fill: TEINTES.charcoal },
      keuros(o.total),
    ),
  );
  svg.appendChild(
    s(
      'text',
      { x: CX, y: CY + 20, 'text-anchor': 'middle', 'font-size': 12, fill: STRUCTURE.etiquette },
      o.sousTitre,
    ),
  );

  // Légende chiffrée : sans elle, l'anneau demande de survoler pour être lu.
  const xLeg = 372;
  const hauteurLigne = 27;
  const yLeg = CY - (parts.length * hauteurLigne) / 2 + 8;
  parts.forEach((p, i) => {
    const y = yLeg + i * hauteurLigne;
    svg.appendChild(s('rect', { x: xLeg, y: y - 9, width: 11, height: 11, rx: 2.5, fill: teinte(i) }));
    svg.appendChild(
      s('text', { x: xLeg + 20, y, 'font-size': 13, fill: TEINTES.slate }, p.libelle),
    );
    svg.appendChild(
      s(
        'text',
        { x: L - 150, y, 'text-anchor': 'end', 'font-size': 13, fill: TEINTES.charcoal },
        euros(p.montant),
      ),
    );
    svg.appendChild(
      s(
        'text',
        { x: L - 26, y, 'text-anchor': 'end', 'font-size': 13, 'font-weight': 600, fill: STRUCTURE.etiquette },
        `${(p.part * 100).toFixed(1).replace('.', ',')} %`,
      ),
    );
  });

  return h(
    'div',
    { classe: 'chart-wrap' },
    svg,
    tableauSimple(
      o.titre,
      [o.enteteAbscisse ?? 'Poste', 'Montant', 'Part'],
      parts.map((p) => [p.libelle, euros(p.montant), `${(p.part * 100).toFixed(1).replace('.', ',')} %`]),
    ),
  );
}

/** Une étape d'un graphique en cascade. */
export interface EtapeCascade {
  readonly libelle: string;
  readonly montant: number;
  /** `borne` = un total posé sur l'axe ; `flux` = une variation empilée. */
  readonly type: 'borne' | 'flux';
}

/**
 * Graphique en cascade — le passage du résultat à la trésorerie.
 *
 * C'est lui qui rend un investissement lisible : sans cascade, la trésorerie
 * baisse et l'on ne sait pas de quoi.
 */
export function graphiqueCascade(
  etapes: readonly EtapeCascade[],
  o: { titre: string; enteteAbscisse: string },
): HTMLElement {
  const L = 880;
  const H = 330;
  const MG = 78;
  const MD = 26;
  const MH = 34;
  const MB = 58;

  // Cumul de chaque étape : une borne repose sur zéro, un flux sur le cumul.
  let cumul = 0;
  const barres = etapes.map((e) => {
    if (e.type === 'borne') {
      cumul = e.montant;
      return { ...e, bas: 0, haut: e.montant };
    }
    const depart = cumul;
    cumul = Math.round((cumul + e.montant) * 100) / 100;
    return { ...e, bas: Math.min(depart, cumul), haut: Math.max(depart, cumul) };
  });

  const toutes = barres.flatMap((b) => [b.bas, b.haut, 0]);
  const max = Math.max(...toutes) * 1.1 || 1;
  const min = Math.min(...toutes, 0) * 1.1;
  const Y = (v: number): number => MH + (H - MH - MB) * (1 - (v - min) / (max - min || 1));
  const creneau = (L - MG - MD) / barres.length;
  const largeur = Math.min(58, creneau * 0.62);

  const svg = s('svg', {
    viewBox: `0 0 ${L} ${H}`,
    role: 'img',
    'aria-label': o.titre,
    'font-family': 'DM Sans, sans-serif',
  });
  svg.appendChild(s('title', {}, o.titre));

  for (let k = 0; k <= 4; k++) {
    const v = min + ((max - min) * k) / 4;
    const y = Y(v);
    svg.appendChild(s('line', { x1: MG, y1: y.toFixed(1), x2: L - MD, y2: y.toFixed(1), stroke: STRUCTURE.grille }));
    svg.appendChild(
      s('text', { x: MG - 10, y: (y + 4).toFixed(1), 'text-anchor': 'end', 'font-size': 11, fill: STRUCTURE.etiquette }, keuros(v)),
    );
  }

  barres.forEach((b, i) => {
    const cx = MG + creneau * (i + 0.5);
    const couleur =
      b.type === 'borne' ? TEINTES.charcoal : b.montant >= 0 ? TEINTES.green : TEINTES.danger;
    const hauteur = Math.max(2, Y(b.bas) - Y(b.haut));
    const r = s('rect', {
      x: (cx - largeur / 2).toFixed(1),
      y: Y(b.haut).toFixed(1),
      width: largeur.toFixed(1),
      height: hauteur.toFixed(1),
      rx: 3,
      fill: couleur,
    });
    r.appendChild(s('title', {}, `${b.libelle} : ${euros(b.montant)}`));
    svg.appendChild(r);

    // Filet de liaison vers la barre suivante : la cascade se lit d'un trait.
    if (i < barres.length - 1) {
      const suivante = barres[i + 1];
      if (suivante && suivante.type === 'flux') {
        const yLien = Y(b.type === 'borne' ? b.montant : b.montant >= 0 ? b.haut : b.bas);
        svg.appendChild(
          s('line', {
            x1: (cx + largeur / 2).toFixed(1),
            y1: yLien.toFixed(1),
            x2: (cx + creneau - largeur / 2).toFixed(1),
            y2: yLien.toFixed(1),
            stroke: STRUCTURE.axe,
            'stroke-width': 1,
            'stroke-dasharray': '3 3',
          }),
        );
      }
    }

    svg.appendChild(
      s(
        'text',
        {
          x: cx.toFixed(1),
          y: (Y(b.haut) - 8).toFixed(1),
          'text-anchor': 'middle',
          'font-size': 11.5,
          'font-weight': 600,
          fill: couleur,
        },
        `${b.type === 'flux' && b.montant > 0 ? '+' : ''}${keuros(b.montant)}`,
      ),
    );
    svg.appendChild(
      s(
        'text',
        { x: cx.toFixed(1), y: H - MB + 20, 'text-anchor': 'middle', 'font-size': 11, fill: STRUCTURE.etiquette },
        b.libelle,
      ),
    );
  });

  svg.appendChild(
    s('line', { x1: MG, y1: Y(0).toFixed(1), x2: L - MD, y2: Y(0).toFixed(1), stroke: STRUCTURE.axe }),
  );

  return h(
    'div',
    { classe: 'chart-wrap' },
    svg,
    tableauSimple(
      o.titre,
      [o.enteteAbscisse, 'Montant', 'Cumul'],
      barres.map((b) => [b.libelle, euros(b.montant), euros(b.type === 'borne' ? b.montant : b.haut === b.bas ? b.haut : b.montant >= 0 ? b.haut : b.bas)]),
    ),
  );
}

/**
 * Graphique en barres empilées : la déformation d'une structure.
 *
 * Les valeurs négatives s'empilent sous l'axe. Le besoin en fonds de roulement
 * en a besoin — créances au-dessus, dettes en dessous — et une structure de
 * produits n'y perd rien. Une série `ligne` facultative se superpose : c'est le
 * net qui se lit par-dessus ses composantes.
 */
export function graphiqueEmpile(
  annees: readonly number[],
  series: readonly { nom: string; couleur: string; valeurs: readonly number[] }[],
  o: {
    titre: string;
    enteteAbscisse: string;
    ligne?: { nom: string; couleur: string; valeurs: readonly number[] };
  },
): HTMLElement {
  const L = 880;
  const H = 330;
  const MG = 78;
  const MD = 26;
  const MH = 26;
  const MB = 44;

  const cumulSigne = (i: number, positif: boolean): number =>
    series.reduce((s2, se) => {
      const v = se.valeurs[i] ?? 0;
      return positif ? s2 + Math.max(0, v) : s2 + Math.min(0, v);
    }, 0);

  const hauts = annees.map((_, i) => cumulSigne(i, true));
  const bassins = annees.map((_, i) => cumulSigne(i, false));
  if (o.ligne) {
    hauts.push(...o.ligne.valeurs.map((v) => Math.max(0, v)));
    bassins.push(...o.ligne.valeurs.map((v) => Math.min(0, v)));
  }
  const max = Math.max(...hauts, 1) * 1.14;
  const min = Math.min(...bassins, 0) * 1.14;
  const Y = (v: number): number => MH + (H - MH - MB) * (1 - (v - min) / (max - min || 1));
  const creneau = (L - MG - MD) / annees.length;
  const largeur = Math.min(62, creneau * 0.5);

  const svg = s('svg', {
    viewBox: `0 0 ${L} ${H}`,
    role: 'img',
    'aria-label': o.titre,
    'font-family': 'DM Sans, sans-serif',
  });
  svg.appendChild(s('title', {}, o.titre));

  for (let k = 0; k <= 4; k++) {
    const v = min + ((max - min) * k) / 4;
    const y = Y(v);
    svg.appendChild(s('line', { x1: MG, y1: y.toFixed(1), x2: L - MD, y2: y.toFixed(1), stroke: STRUCTURE.grille }));
    svg.appendChild(
      s('text', { x: MG - 10, y: (y + 4).toFixed(1), 'text-anchor': 'end', 'font-size': 11, fill: STRUCTURE.etiquette }, keuros(v)),
    );
  }

  annees.forEach((an, i) => {
    const cx = MG + creneau * (i + 0.5);
    let hautPositif = 0;
    let basNegatif = 0;
    for (const se of series) {
      const v = se.valeurs[i] ?? 0;
      if (v === 0) continue;
      const [de, a] = v > 0 ? [hautPositif, hautPositif + v] : [basNegatif + v, basNegatif];
      if (v > 0) hautPositif = a;
      else basNegatif = de;
      const r = s('rect', {
        x: (cx - largeur / 2).toFixed(1),
        y: Y(a).toFixed(1),
        width: largeur.toFixed(1),
        height: Math.max(1, Y(de) - Y(a)).toFixed(1),
        fill: se.couleur,
      });
      r.appendChild(s('title', {}, `${an} — ${se.nom} : ${euros(v)}`));
      svg.appendChild(r);
    }
    svg.appendChild(
      s(
        'text',
        { x: cx.toFixed(1), y: (Y(hautPositif) - 8).toFixed(1), 'text-anchor': 'middle', 'font-size': 11.5, 'font-weight': 600, fill: TEINTES.charcoal },
        keuros(hautPositif),
      ),
    );
    svg.appendChild(
      s('text', { x: cx.toFixed(1), y: H - MB + 22, 'text-anchor': 'middle', 'font-size': 11.5, fill: STRUCTURE.etiquette }, String(an)),
    );
  });

  svg.appendChild(s('line', { x1: MG, y1: Y(0).toFixed(1), x2: L - MD, y2: Y(0).toFixed(1), stroke: STRUCTURE.axe }));

  if (o.ligne) {
    const points = annees
      .map((_, i) => `${(MG + creneau * (i + 0.5)).toFixed(1)},${Y(o.ligne?.valeurs[i] ?? 0).toFixed(1)}`)
      .join(' ');
    svg.appendChild(
      s('polyline', { points, fill: 'none', stroke: o.ligne.couleur, 'stroke-width': 2.5, 'stroke-linejoin': 'round' }),
    );
    annees.forEach((an, i) => {
      const v = o.ligne?.valeurs[i] ?? 0;
      const c = s('circle', {
        cx: (MG + creneau * (i + 0.5)).toFixed(1),
        cy: Y(v).toFixed(1),
        r: 4.5,
        fill: STRUCTURE.fond,
        stroke: o.ligne?.couleur ?? TEINTES.rose,
        'stroke-width': 2,
      });
      c.appendChild(s('title', {}, `${an} — ${o.ligne?.nom ?? ''} : ${euros(v)}`));
      svg.appendChild(c);
    });
  }

  return h(
    'div',
    { classe: 'chart-wrap' },
    svg,
    tableauDonnees(
      o.titre,
      o.enteteAbscisse,
      annees,
      [
        ...series.map((se) => ({ nom: se.nom, couleur: se.couleur, valeurs: se.valeurs })),
        ...(o.ligne ? [{ nom: o.ligne.nom, couleur: o.ligne.couleur, valeurs: o.ligne.valeurs }] : []),
      ],
      'euros',
    ),
  );
}

/**
 * Point mort : à partir de combien d'adhérents payeurs l'exercice s'équilibre.
 *
 * L'axe des abscisses porte l'effectif, non le temps : les produits croissent
 * avec lui, les charges n'en dépendent pas. Le croisement est le point mort.
 */
export function graphiquePointMort(o: {
  readonly titre: string;
  readonly exercice: number;
  readonly charges: number;
  readonly produitsFixes: number;
  readonly produitMoyen: number;
  readonly effectifProjete: number;
  readonly adherentsPointMort: number;
  readonly indetermine: boolean;
}): HTMLElement {
  const L = 880;
  const H = 330;
  const MG = 78;
  const MD = 26;
  const MH = 26;
  const MB = 52;

  if (o.indetermine) {
    return h(
      'div',
      { classe: 'chart-wrap' },
      h('p', { classe: 'note-lecture' }, 'Produit moyen nul : le point mort n’a pas de sens sur cet exercice.'),
    );
  }

  const nMax = Math.max(o.adherentsPointMort, o.effectifProjete, 1) * 1.35;
  const produits = (n: number): number => o.produitsFixes + n * o.produitMoyen;
  const vMax = Math.max(produits(nMax), o.charges) * 1.08;

  const X = (n: number): number => MG + (L - MG - MD) * (n / nMax);
  const Y = (v: number): number => MH + (H - MH - MB) * (1 - v / (vMax || 1));

  const svg = s('svg', {
    viewBox: `0 0 ${L} ${H}`,
    role: 'img',
    'aria-label': o.titre,
    'font-family': 'DM Sans, sans-serif',
  });
  svg.appendChild(s('title', {}, o.titre));

  for (let k = 0; k <= 4; k++) {
    const v = (vMax * k) / 4;
    const y = Y(v);
    svg.appendChild(s('line', { x1: MG, y1: y.toFixed(1), x2: L - MD, y2: y.toFixed(1), stroke: STRUCTURE.grille }));
    svg.appendChild(
      s('text', { x: MG - 10, y: (y + 4).toFixed(1), 'text-anchor': 'end', 'font-size': 11, fill: STRUCTURE.etiquette }, keuros(v)),
    );
  }

  svg.appendChild(
    s('polyline', {
      points: `${X(0).toFixed(1)},${Y(produits(0)).toFixed(1)} ${X(nMax).toFixed(1)},${Y(produits(nMax)).toFixed(1)}`,
      fill: 'none',
      stroke: TEINTES.green,
      'stroke-width': 2.5,
    }),
  );
  svg.appendChild(
    s('polyline', {
      points: `${X(0).toFixed(1)},${Y(o.charges).toFixed(1)} ${X(nMax).toFixed(1)},${Y(o.charges).toFixed(1)}`,
      fill: 'none',
      stroke: TEINTES.danger,
      'stroke-width': 2.5,
    }),
  );

  const xPm = X(o.adherentsPointMort);
  svg.appendChild(
    s('line', { x1: xPm.toFixed(1), y1: Y(o.charges).toFixed(1), x2: xPm.toFixed(1), y2: Y(0).toFixed(1), stroke: TEINTES.rose, 'stroke-width': 1, 'stroke-dasharray': '4 4' }),
  );
  const pastille = s('circle', {
    cx: xPm.toFixed(1),
    cy: Y(o.charges).toFixed(1),
    r: 6,
    fill: STRUCTURE.fond,
    stroke: TEINTES.rose,
    'stroke-width': 2.5,
  });
  pastille.appendChild(s('title', {}, `Point mort : ${entier(o.adherentsPointMort)} adhérents payeurs`));
  svg.appendChild(pastille);
  svg.appendChild(
    s(
      'text',
      { x: xPm.toFixed(1), y: H - MB + 20, 'text-anchor': 'middle', 'font-size': 12, 'font-weight': 600, fill: TEINTES.rose },
      `${entier(o.adherentsPointMort)} adhérents`,
    ),
  );

  // Effectif retenu par le scénario : la distance au point mort est le message.
  const xProj = X(o.effectifProjete);
  svg.appendChild(
    s('line', { x1: xProj.toFixed(1), y1: MH, x2: xProj.toFixed(1), y2: Y(0).toFixed(1), stroke: TEINTES.blue, 'stroke-width': 1.5, 'stroke-dasharray': '2 4' }),
  );
  svg.appendChild(
    s(
      'text',
      { x: xProj.toFixed(1), y: H - MB + 36, 'text-anchor': 'middle', 'font-size': 11, fill: TEINTES.blue },
      `projeté : ${entier(o.effectifProjete)}`,
    ),
  );

  svg.appendChild(s('line', { x1: MG, y1: Y(0).toFixed(1), x2: L - MD, y2: Y(0).toFixed(1), stroke: STRUCTURE.axe }));
  svg.appendChild(
    s('text', { x: MG + 6, y: (Y(produits(nMax * 0.22)) - 8).toFixed(1), 'font-size': 12, fill: TEINTES.green }, 'produits'),
  );
  svg.appendChild(
    s('text', { x: MG + 6, y: (Y(o.charges) - 8).toFixed(1), 'font-size': 12, fill: TEINTES.danger }, 'charges'),
  );

  return h(
    'div',
    { classe: 'chart-wrap' },
    svg,
    tableauSimple(
      o.titre,
      ['Grandeur', `Exercice ${o.exercice}`],
      [
        ['Charges à couvrir', euros(o.charges)],
        ['Produits indépendants de l’effectif', euros(o.produitsFixes)],
        ['Produit moyen par adhérent', euros(o.produitMoyen)],
        ['Adhérents payeurs à l’équilibre', entier(o.adherentsPointMort)],
        ['Effectif retenu par le scénario', entier(o.effectifProjete)],
      ],
    ),
  );
}
